// フィルター定義。パラメータのスキーマからダイアログを自動生成する。
// 実行は filters/gpu.ts (GPU) が担い、engine が FilterDef.passes を順に実行する。

import type { I18nKey } from '@/i18n'

export type ParamDef =
  | { key: string; label: I18nKey; type: 'number'; min: number; max: number; step: number; default: number; unit?: string }
  | { key: string; label: I18nKey; type: 'boolean'; default: boolean }
  | { key: string; label: I18nKey; type: 'select'; options: { value: string; label: I18nKey }[]; default: string }

export type FilterParams = Record<string, number | boolean | string>

/** 1 パス = シェーダ種別 + パラメータ + 入力の指定 */
export interface FilterPass {
  shader: 'blurDir' | 'motion' | 'combine' | 'point' | 'surface'
  /** uP0 / uP1 の値 (params から計算) */
  p0: [number, number, number, number]
  p1?: [number, number, number, number]
  /** combine 用: 2 番目の入力を「元画像」にする (それ以外は直前の出力) */
  useOriginalAsSrc?: boolean
}

export interface FilterDef {
  id: string
  label: I18nKey
  category: 'blur' | 'sharpen' | 'noise' | 'stylize' | 'pixelate' | 'other'
  params: ParamDef[]
  /** 結果がレイヤーの外へ広がる最大距離 (px)。この分だけ処理範囲を広げる */
  extent: (p: FilterParams) => number
  passes: (p: FilterParams) => FilterPass[]
  /** CPU (Worker) で実行する。指定時は passes は使わない (filters/cpu.ts の runCpuFilter が id で振り分ける) */
  cpu?: true
}

const filters: FilterDef[] = []

function def(f: FilterDef) {
  filters.push(f)
}

export function listFilters(): readonly FilterDef[] {
  return filters
}

export function getFilter(id: string): FilterDef | undefined {
  return filters.find(f => f.id === id)
}

export function defaultParams(f: FilterDef): FilterParams {
  const out: FilterParams = {}
  for (const p of f.params) out[p.key] = p.default
  return out
}

/** パラメータをスキーマで正規化 (範囲・型) */
export function normalizeParams(f: FilterDef, raw: FilterParams | undefined): FilterParams {
  const out = defaultParams(f)
  if (!raw) return out
  for (const p of f.params) {
    const v = raw[p.key]
    if (v === undefined) continue
    if (p.type === 'number' && typeof v === 'number' && Number.isFinite(v)) out[p.key] = Math.max(p.min, Math.min(p.max, v))
    else if (p.type === 'boolean') out[p.key] = !!v
    else if (p.type === 'select' && typeof v === 'string' && p.options.some(o => o.value === v)) out[p.key] = v
  }
  return out
}

const n = (p: FilterParams, k: string) => Number(p[k] ?? 0)
const b = (p: FilterParams, k: string) => !!p[k]

/** 大きな半径は複数回に分けてぼかす (1 パスの上限 96 タップ) */
function blurPasses(radius: number, box: boolean): FilterPass[] {
  const out: FilterPass[] = []
  let remain = radius
  while (remain > 0) {
    const r = Math.min(remain, 64)
    out.push({ shader: 'blurDir', p0: [1, 0, r, box ? 1 : 0] }, { shader: 'blurDir', p0: [0, 1, r, box ? 1 : 0] })
    remain -= r
  }
  return out
}

def({ id: 'gaussianBlur', label: 'filter.gaussianBlur', category: 'blur', params: [{ key: 'radius', label: 'filter.p.radius', type: 'number', min: 0, max: 250, step: 0.1, default: 5, unit: 'px' }], extent: p => Math.ceil(n(p, 'radius') * 2 + 1), passes: p => blurPasses(n(p, 'radius'), false) })
def({ id: 'boxBlur', label: 'filter.boxBlur', category: 'blur', params: [{ key: 'radius', label: 'filter.p.radius', type: 'number', min: 1, max: 250, step: 1, default: 5, unit: 'px' }], extent: p => Math.ceil(n(p, 'radius') + 1), passes: p => blurPasses(n(p, 'radius'), true) })
def({
  id: 'motionBlur', label: 'filter.motionBlur', category: 'blur',
  params: [{ key: 'angle', label: 'filter.p.angle', type: 'number', min: -180, max: 180, step: 1, default: 0, unit: '°' }, { key: 'distance', label: 'filter.p.distance', type: 'number', min: 1, max: 500, step: 1, default: 20, unit: 'px' }],
  extent: p => Math.ceil(n(p, 'distance') / 2 + 1),
  passes: p => { const a = (n(p, 'angle') * Math.PI) / 180; return [{ shader: 'motion', p0: [Math.cos(a), -Math.sin(a), n(p, 'distance'), 0] }] }
})
def({ id: 'surfaceBlur', label: 'filter.surfaceBlur', category: 'blur', params: [{ key: 'radius', label: 'filter.p.radius', type: 'number', min: 1, max: 12, step: 1, default: 5, unit: 'px' }, { key: 'threshold', label: 'filter.p.threshold', type: 'number', min: 1, max: 255, step: 1, default: 15 }], extent: p => n(p, 'radius') + 1, passes: p => [{ shader: 'surface', p0: [n(p, 'radius'), n(p, 'threshold') / 255, 0, 0] }] })
def({ id: 'sharpen', label: 'filter.sharpen', category: 'sharpen', params: [{ key: 'amount', label: 'filter.p.amount', type: 'number', min: 1, max: 500, step: 1, default: 50, unit: '%' }], extent: () => 1, passes: p => [{ shader: 'point', p0: [6, n(p, 'amount') / 100, 0, 0] }] })
def({
  id: 'unsharpMask', label: 'filter.unsharpMask', category: 'sharpen',
  params: [{ key: 'amount', label: 'filter.p.amount', type: 'number', min: 1, max: 500, step: 1, default: 100, unit: '%' }, { key: 'radius', label: 'filter.p.radius', type: 'number', min: 0.1, max: 250, step: 0.1, default: 1, unit: 'px' }, { key: 'threshold', label: 'filter.p.threshold', type: 'number', min: 0, max: 255, step: 1, default: 0 }],
  extent: p => Math.ceil(n(p, 'radius') * 2 + 1),
  passes: p => [...blurPasses(n(p, 'radius'), false), { shader: 'combine', p0: [0, n(p, 'amount') / 100, n(p, 'threshold') / 255, 0], useOriginalAsSrc: true }]
})
def({ id: 'highPass', label: 'filter.highPass', category: 'other', params: [{ key: 'radius', label: 'filter.p.radius', type: 'number', min: 0.1, max: 250, step: 0.1, default: 10, unit: 'px' }], extent: p => Math.ceil(n(p, 'radius') * 2 + 1), passes: p => [...blurPasses(n(p, 'radius'), false), { shader: 'combine', p0: [1, 0, 0, 0], useOriginalAsSrc: true }] })
def({ id: 'addNoise', label: 'filter.addNoise', category: 'noise', params: [{ key: 'amount', label: 'filter.p.amount', type: 'number', min: 0.1, max: 100, step: 0.1, default: 12.5, unit: '%' }, { key: 'monochrome', label: 'filter.p.monochrome', type: 'boolean', default: false }], extent: () => 0, passes: p => [{ shader: 'point', p0: [1, n(p, 'amount') / 100, b(p, 'monochrome') ? 1 : 0, 0], p1: [Math.random() * 1000, 0, 0, 0] }] })
def({ id: 'pixelate', label: 'filter.pixelate', category: 'pixelate', params: [{ key: 'cell', label: 'filter.p.cell', type: 'number', min: 2, max: 200, step: 1, default: 10, unit: 'px' }], extent: p => n(p, 'cell'), passes: p => [{ shader: 'point', p0: [2, n(p, 'cell'), 0, 0] }] })
def({ id: 'emboss', label: 'filter.emboss', category: 'stylize', params: [{ key: 'angle', label: 'filter.p.angle', type: 'number', min: -180, max: 180, step: 1, default: 135, unit: '°' }, { key: 'height', label: 'filter.p.height', type: 'number', min: 1, max: 100, step: 1, default: 3, unit: 'px' }, { key: 'amount', label: 'filter.p.amount', type: 'number', min: 1, max: 500, step: 1, default: 100, unit: '%' }], extent: p => n(p, 'height') + 1, passes: p => [{ shader: 'point', p0: [3, (n(p, 'angle') * Math.PI) / 180, n(p, 'height'), n(p, 'amount') / 100] }] })
def({ id: 'findEdges', label: 'filter.findEdges', category: 'stylize', params: [], extent: () => 1, passes: () => [{ shader: 'point', p0: [4, 0, 0, 0] }] })
def({ id: 'invert', label: 'filter.invert', category: 'other', params: [], extent: () => 0, passes: () => [{ shader: 'point', p0: [0, 0, 0, 0] }] })
def({ id: 'desaturate', label: 'filter.desaturate', category: 'other', params: [], extent: () => 0, passes: () => [{ shader: 'point', p0: [7, 0, 0, 0] }] })
def({ id: 'vignette', label: 'filter.vignette', category: 'stylize', params: [{ key: 'amount', label: 'filter.p.amount', type: 'number', min: 0, max: 100, step: 1, default: 50, unit: '%' }, { key: 'radius', label: 'filter.p.radius', type: 'number', min: 0, max: 150, step: 1, default: 60, unit: '%' }, { key: 'softness', label: 'filter.p.softness', type: 'number', min: 0, max: 100, step: 1, default: 50, unit: '%' }], extent: () => 0, passes: p => [{ shader: 'point', p0: [5, n(p, 'amount') / 100, n(p, 'radius') / 100, n(p, 'softness') / 100], p1: [NaN, NaN, 0, 0] }] })

// --- CPU (Worker) フィルター ---
def({ id: 'median', label: 'filter.median', category: 'noise', cpu: true, params: [{ key: 'radius', label: 'filter.p.radius', type: 'number', min: 1, max: 16, step: 1, default: 2, unit: 'px' }], extent: p => n(p, 'radius'), passes: () => [] })
def({ id: 'despeckle', label: 'filter.despeckle', category: 'noise', cpu: true, params: [], extent: () => 1, passes: () => [] })
def({ id: 'dustScratches', label: 'filter.dustScratches', category: 'noise', cpu: true, params: [{ key: 'radius', label: 'filter.p.radius', type: 'number', min: 1, max: 16, step: 1, default: 2, unit: 'px' }, { key: 'threshold', label: 'filter.p.threshold', type: 'number', min: 0, max: 255, step: 1, default: 0 }], extent: p => n(p, 'radius'), passes: () => [] })
def({ id: 'reduceNoise', label: 'filter.reduceNoise', category: 'noise', cpu: true, params: [{ key: 'strength', label: 'filter.p.strength', type: 'number', min: 1, max: 10, step: 1, default: 5 }, { key: 'preserveDetails', label: 'filter.p.preserveDetails', type: 'number', min: 0, max: 100, step: 1, default: 60, unit: '%' }], extent: () => 6, passes: () => [] })
def({ id: 'oilPaint', label: 'filter.oilPaint', category: 'stylize', cpu: true, params: [{ key: 'radius', label: 'filter.p.radius', type: 'number', min: 1, max: 12, step: 1, default: 4, unit: 'px' }], extent: p => n(p, 'radius'), passes: () => [] })

export const FILTER_CATEGORIES: { id: FilterDef['category']; label: I18nKey }[] = [
  { id: 'blur', label: 'filter.cat.blur' },
  { id: 'sharpen', label: 'filter.cat.sharpen' },
  { id: 'noise', label: 'filter.cat.noise' },
  { id: 'pixelate', label: 'filter.cat.pixelate' },
  { id: 'stylize', label: 'filter.cat.stylize' },
  { id: 'other', label: 'filter.cat.other' }
]
